import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

export interface OperatorTenant {
    id: string;
    name: string;
    slug: string;
    status: string;
    memberCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface OperatorEntitlement {
    planName: string;
    status: string;
    enabledFeatures: string[];
    seatCap: number | null;
}

export interface ProvisionInput {
    tenantId: string;
    tenantName: string;
    adminEmail?: string;
}

/** Everything the operator needs to hand the new admin their first login. */
export interface ProvisionHandoff {
    adminEmail?: string;
    emailSent: boolean;
    loginUrl: string;
}

export interface EntitlementInput {
    planName: string;
    status: string;
    enabledFeatures: string[];
    seatCap?: number;
}

async function readError(res: Response, fallback: string): Promise<string> {
    try {
        const body = (await res.json()) as { error?: string };
        if (body.error) return body.error;
    } catch { /* keep fallback */ }
    return fallback;
}

/**
 * Data + actions for the operator tenants console (SAAS-32). Reads the
 * cross-tenant list from the operator-gated auth surface and drives the tenant
 * lifecycle + entitlement endpoints. All calls go through the BFF, which attaches
 * the operator bearer and CSRF token.
 */
@injectable()
export class OperatorTenantsService {
    async listTenants(): Promise<OperatorTenant[]> {
        const res = await authenticatedFetch('/_auth/api/operator/tenants');
        if (!res.ok) throw new Error(await readError(res, `Failed to load tenants (${res.status})`));
        return (await res.json()) as OperatorTenant[];
    }

    async provision(input: ProvisionInput): Promise<ProvisionHandoff | null> {
        const { tenantId, ...body } = input;
        const result = await this.postJson<{ handoff?: ProvisionHandoff }>(
            `/_auth/api/tenants/${encodeURIComponent(tenantId)}/provision`, body, 'Provisioning failed',
        );
        return result.handoff ?? null;
    }

    async resendInvite(tenantId: string): Promise<{ adminEmail: string; emailSent: boolean }> {
        return this.postJson(`/_auth/api/tenants/${encodeURIComponent(tenantId)}/resend-invite`, {}, 'Resend failed');
    }

    async generateAdminTempPassword(tenantId: string): Promise<{ adminEmail: string; password: string }> {
        return this.postJson(`/_auth/api/tenants/${encodeURIComponent(tenantId)}/admin-temp-password`, {}, 'Failed to generate password');
    }

    async suspend(tenantId: string, reason?: string): Promise<void> {
        await this.post(`/_auth/api/tenants/${encodeURIComponent(tenantId)}/suspend`, { reason }, 'Suspend failed');
    }

    async resume(tenantId: string): Promise<void> {
        await this.post(`/_auth/api/tenants/${encodeURIComponent(tenantId)}/resume`, {}, 'Resume failed');
    }

    async offboard(tenantId: string, reason?: string): Promise<void> {
        await this.post(`/_auth/api/tenants/${encodeURIComponent(tenantId)}/offboard`, { reason }, 'Offboard failed');
    }

    async getEntitlement(tenantId: string): Promise<OperatorEntitlement | null> {
        const res = await authenticatedFetch(`/_billing/api/operator/billing/entitlements/${encodeURIComponent(tenantId)}`);
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(await readError(res, `Failed to load entitlement (${res.status})`));
        return (await res.json()) as OperatorEntitlement;
    }

    async saveEntitlement(tenantId: string, input: EntitlementInput): Promise<void> {
        const res = await authenticatedFetch(`/_billing/api/operator/billing/entitlements/${encodeURIComponent(tenantId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to save entitlement (${res.status})`));
    }

    private async post(url: string, body: unknown, fallback: string): Promise<void> {
        const res = await authenticatedFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body ?? {}),
        });
        if (!res.ok) throw new Error(await readError(res, `${fallback} (${res.status})`));
    }

    private async postJson<T>(url: string, body: unknown, fallback: string): Promise<T> {
        const res = await authenticatedFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body ?? {}),
        });
        if (!res.ok) throw new Error(await readError(res, `${fallback} (${res.status})`));
        return (await res.json()) as T;
    }
}
